import React from 'react';
import { observer } from 'mobx-react-lite';
import {
    ListContainer,
    ErrorMessageTop,
    ListWrapper,
    ListContentWrapper,
} from 'src/domains/layouts/shared/cms/list/List.style';
import { PromoCarouselListItem } from './PromoCarouselList/PromoCarouselListItem';
import { HomePageCarouselState } from 'src/AppState/CmsState/HomePageCarouselState/HomePageCarouselState';
import { ColumnOneOfTwo, TwoColumnResponsiveContentReverse } from 'src/domains/layouts/shared/layout/Layout.style';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';
import { EmptyListItem } from 'src/domains/layouts/shared/cms/list/listItems/EmptyListItem';
import { ListHeader } from 'src/domains/layouts/shared/cms/list/listHeader/ListHeader';
import { List as MovableList } from 'react-movable';
import { HomePageCarouselModelType } from 'src/api/config/home_page_carousel/getHomePageCarousel';
import { FormWrapper } from 'src/domains/layouts/shared/cms/form/Form.style';
import { HomePageCarouselFilter } from 'src/main/cms/HomePageCarousel/HomePageCarouselForm/HomePageCarouselFilter';
import { ParagraphDefault } from 'src/domains/layouts/shared/typography/Content.style';
import { Button } from 'src/domains/common/components/button/button';
import { TrashIcon } from 'lucide-react';
import {
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from 'src/domains/common/components/dialog/dialog';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from 'src/domains/common/components/table/table';
import { Checkbox } from 'src/domains/common/components/checkbox/checkbox';
import { format, isAfter, isBefore, isValid } from 'date-fns';

const formatDisplayDate = (dateValue: string): string => {
    if (dateValue === '') {
        return '-';
    }

    const date = new Date(dateValue);
    return isValid(date) ? format(date, 'dd/MM/yyyy') : '-';
};

interface PromoCarouselContentPropsType {
    promoCarouselState: HomePageCarouselState;
}

export const PromoCarouselContent = observer(({ promoCarouselState }: PromoCarouselContentPropsType) => {
    const {
        errorMessageForList,
        isLoading,
        promoCarouselList,
        promoCarouselSorted,
        promoCarouselExpired,
        HomePageCarouselView,
        handleListOrder,
        showBulkDeleteDialog,
        selectedItemsForBulkDelete,
        handleShowBulkDeleteDialog,
        handleHideBulkDeleteDialog,
        handleToggleItemSelection,
        handleToggleAllItems,
        handleConfirmBulkDelete,
    } = promoCarouselState;

    const getStatusLabel = (el: HomePageCarouselModelType): 'live' | 'scheduled' | 'ended' | '' => {
        if (el.date_from === '' || el.date_to === '') {
            return '';
        }

        const dateFrom = new Date(el.date_from);
        const dateTo = new Date(el.date_to);

        if (!isValid(dateFrom) || !isValid(dateTo)) {
            return '';
        }

        const currentDate = new Date();
        if (isBefore(currentDate, dateFrom)) {
            return 'scheduled';
        }

        if (isAfter(currentDate, dateTo)) {
            return 'ended';
        }

        return 'live';
    };

    return (
        <TwoColumnResponsiveContentReverse>
            <ColumnOneOfTwo>
                {errorMessageForList !== null && <ErrorMessageTop>{errorMessageForList}</ErrorMessageTop>}
                <ListWrapper>
                    <ListHeader label='Promo Carousels'>
                        <>
                            <ParagraphDefault>
                                The list items below are referring to cards in the promo carousel tray that sit at the
                                top of the live site. To change the order in which they appear on the live site, click
                                and drag the use drag&drop icon visible on the left of each list element.
                            </ParagraphDefault>
                            <Button
                                variant='destructive'
                                className='w-fit'
                                onClick={handleShowBulkDeleteDialog}
                            >
                                <TrashIcon /> Bulk Delete
                            </Button>
                        </>
                    </ListHeader>
                    <ListContainer
                        data-test='homepage-list-container'
                        areLoading={isLoading}
                    >
                        <Loader isActive={isLoading} />

                        {promoCarouselList.length === 0 ? (
                            <EmptyListItem />
                        ) : (
                            <MovableList
                                values={promoCarouselSorted}
                                onChange={handleListOrder}
                                renderList={({ children, props, isDragged }): React.ReactNode => (
                                    <ListContentWrapper
                                        {...props}
                                        isDragged={isDragged}
                                    >
                                        {children}
                                    </ListContentWrapper>
                                )}
                                renderItem={({ value, props }): JSX.Element | null => {
                                    return (
                                        <div
                                            {...props}
                                            style={{ ...props.style, zIndex: 'initial' }}
                                        >
                                            <PromoCarouselListItem
                                                key={value.id}
                                                homePageCarouselItem={value}
                                                carouselPromoState={promoCarouselState}
                                            />
                                        </div>
                                    );
                                }}
                            />
                        )}

                        {promoCarouselExpired.map((homePageCarouselItem: HomePageCarouselModelType) => {
                            return (
                                <PromoCarouselListItem
                                    key={homePageCarouselItem.id}
                                    homePageCarouselItem={homePageCarouselItem}
                                    carouselPromoState={promoCarouselState}
                                />
                            );
                        })}
                    </ListContainer>
                </ListWrapper>
            </ColumnOneOfTwo>

            <ColumnOneOfTwo>
                {HomePageCarouselView === null ? (
                    <FormWrapper>
                        <HomePageCarouselFilter carouselPromoState={promoCarouselState} />
                    </FormWrapper>
                ) : (
                    <HomePageCarouselView
                        dataTest='homepage-carousel'
                        carouselPromoState={promoCarouselState}
                    />
                )}
            </ColumnOneOfTwo>

            {showBulkDeleteDialog && (
                <Dialog
                    onClickOutside={handleHideBulkDeleteDialog}
                    dataTest='bulk-delete-dialog'
                    className='max-w-4xl max-h-[80vh] overflow-auto'
                >
                    <DialogHeader>
                        <DialogTitle>Bulk Delete Carousel Items</DialogTitle>
                        <DialogDescription>
                            Select the carousel items you want to delete. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>

                    <div className='mt-4'>
                        {promoCarouselList.length === 0 ? (
                            <ParagraphDefault>No carousel items available to delete.</ParagraphDefault>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className='w-12'>
                                            <Checkbox
                                                checked={
                                                    selectedItemsForBulkDelete.length === promoCarouselList.length &&
                                                    promoCarouselList.length > 0
                                                }
                                                indeterminate={
                                                    selectedItemsForBulkDelete.length > 0 &&
                                                    selectedItemsForBulkDelete.length < promoCarouselList.length
                                                }
                                                onChange={(e) => handleToggleAllItems(e.target.checked)}
                                                dataTest='select-all-checkbox'
                                            />
                                        </TableHead>
                                        <TableHead>ID</TableHead>
                                        <TableHead>Title</TableHead>
                                        <TableHead>Date From</TableHead>
                                        <TableHead>Date To</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {promoCarouselList.map((item) => {
                                        const formattedDateFrom = formatDisplayDate(item.date_from);
                                        const formattedDateTo = formatDisplayDate(item.date_to);

                                        return (
                                            <TableRow
                                                key={item.id}
                                                dataTest={`carousel-item-${item.id}`}
                                            >
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedItemsForBulkDelete.includes(item.id)}
                                                        onChange={() => handleToggleItemSelection(item.id)}
                                                        dataTest={`select-item-${item.id}`}
                                                    />
                                                </TableCell>
                                                <TableCell>{item.id}</TableCell>
                                                <TableCell>{item.title}</TableCell>
                                                <TableCell>{formattedDateFrom}</TableCell>
                                                <TableCell>{formattedDateTo}</TableCell>
                                                <TableCell>{getStatusLabel(item)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant='outline'
                            onClick={handleHideBulkDeleteDialog}
                            dataTest='cancel-bulk-delete'
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='destructive'
                            onClick={handleConfirmBulkDelete}
                            disabled={selectedItemsForBulkDelete.length === 0}
                            dataTest='confirm-bulk-delete'
                        >
                            Delete Selected ({selectedItemsForBulkDelete.length})
                        </Button>
                    </DialogFooter>
                </Dialog>
            )}
        </TwoColumnResponsiveContentReverse>
    );
});
